"use client";

import React from "react";
import { usePathname } from "next/navigation";
import { useAuth } from "@/context/AuthContext";
import { isProtectedPath } from "@/lib/auth/authorized-pages";
import { ShieldAlert, Loader2 } from "lucide-react";
import Link from "next/link";

export const RoleGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const pathname = usePathname();
  const { permissions, isLoading } = useAuth();
  const protectedPath = isProtectedPath(pathname);
  const authorized = permissions.canAccessPath(pathname);

  if (isLoading && protectedPath) {
    return (
      <div className="flex h-[50vh] items-center justify-center bg-background text-foreground">
        <div className="text-center space-y-2 flex flex-col items-center">
          <Loader2 className="animate-spin text-primary" size={24} />
          <p className="text-[10px] uppercase tracking-widest font-mono text-foreground/50">
            Authorizing access...
          </p>
        </div>
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="max-w-md mx-auto my-20 p-8 border border-red-500/30 bg-red-500/5 text-center space-y-6">
        <ShieldAlert className="w-12 h-12 text-red-500 mx-auto" />
        <h1 className="text-lg font-bold uppercase tracking-widest text-red-700 dark:text-red-400">
          Access Denied
        </h1>
        <p className="text-xs text-foreground/75 leading-relaxed font-sans">
          This zone is restricted to authorized roles. Your current credentials do not grant access to this registry.
        </p>
        <div className="border-t border-border/40 my-4 pt-4" />
        <div className="flex justify-center gap-4 font-sans">
          <Link
            href="/profile"
            className="bg-foreground text-background font-bold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition cursor-pointer"
          >
            Manage Profile
          </Link>
          <Link
            href="/"
            className="border border-border font-bold px-6 py-2.5 text-xs uppercase tracking-widest hover:bg-foreground hover:text-background transition cursor-pointer"
          >
            Home
          </Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
export default RoleGuard;
